import {
  BarChart,
  EmptyState,
  List,
  ListRow,
  Section,
  Select,
  Skeleton,
  StatCard,
  Text,
} from '@ui';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '../../../lib/errors';
import { useUrlState } from '../../../lib/useUrlState';
import { change } from '../../reports/format';
import { formatEuro } from '../../sales/format';
import { exportUrl, useFinanceOverview, type FinanceOverview } from '../api';
import { FinanceActions, useRecordEntry } from '../components/FinanceActions';
import styles from './FinancePages.module.css';

const monthShort = new Intl.DateTimeFormat('de-DE', { month: 'short' });
const monthLong = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' });

/** Income, expenses and result of a year (FIN-005). */
export function FinanceOverviewPage() {
  const { t } = useTranslation();
  const thisYear = new Date().getFullYear();
  const [yearParam, setYear] = useUrlState('jahr', String(thisYear));
  const year = Number(yearParam) || thisYear;
  const overview = useFinanceOverview(year);
  const { record, dialog } = useRecordEntry();
  const years = Array.from(new Set([thisYear, year, ...(overview.data?.years ?? [])])).sort(
    (a, b) => b - a,
  );

  return (
    <div className={styles.page}>
      <FinanceActions
        onRecord={record}
        exportHref={exportUrl({ from: `${year}-01-01`, to: `${year}-12-31` })}
      />
      <div className={styles.year}>
        <Select
          size="sm"
          aria-label={t('finance.overview.year')}
          value={String(year)}
          onChange={(e) => setYear(e.target.value)}
          options={years.map((y) => ({ value: String(y), label: String(y) }))}
        />
      </div>
      {overview.error ? (
        <Text tone="danger" role="alert">
          {errorMessage(overview.error)}
        </Text>
      ) : !overview.data ? (
        <div role="status" aria-label={t('common.loading')}>
          <Skeleton height="20rem" shape="rect" />
        </div>
      ) : (
        <Overview data={overview.data} />
      )}
      {dialog}
    </div>
  );
}

function Overview({ data }: { data: FinanceOverview }) {
  const { t } = useTranslation();
  const previous = (cents: number) =>
    t('finance.overview.previousYear', { year: data.year - 1, amount: formatEuro(cents) });
  const empty = data.totals.income_cents === 0 && data.totals.expense_cents === 0;
  const months = data.months.map((m) => new Date(data.year, m.month - 1, 1));
  return (
    <>
      <section aria-label={t('finance.overview.figures')} className={styles.figures}>
        <StatCard
          label={t('finance.overview.income')}
          value={formatEuro(data.totals.income_cents)}
          delta={change(
            data.totals.income_cents,
            data.previous.income_cents,
            t('finance.overview.vsPrevious', { year: data.year - 1 }),
          )}
          footnote={previous(data.previous.income_cents)}
        />
        <StatCard
          label={t('finance.overview.expenses')}
          value={formatEuro(data.totals.expense_cents)}
          footnote={previous(data.previous.expense_cents)}
        />
        <StatCard
          label={t('finance.overview.result')}
          value={formatEuro(data.totals.result_cents)}
          footnote={previous(data.previous.result_cents)}
        />
      </section>
      {empty ? (
        <EmptyState title={t('finance.overview.emptyTitle', { year: data.year })} />
      ) : (
        <>
          <Section title={t('finance.overview.months')} variant="content">
            <BarChart
              label={t('finance.overview.monthsChart', { year: data.year })}
              categoryHeader={t('finance.overview.month')}
              categories={months.map((d) => monthShort.format(d).replace('.', ''))}
              categoryTitles={months.map((d) => monthLong.format(d))}
              format={(v) => formatEuro(v)}
              series={[
                {
                  key: 'income',
                  label: t('finance.overview.income'),
                  values: data.months.map((m) => m.income_cents),
                },
                {
                  key: 'expense',
                  label: t('finance.overview.expenses'),
                  values: data.months.map((m) => m.expense_cents),
                },
              ]}
            />
          </Section>
          <div className={styles.columns}>
            {(['income', 'expense'] as const).map((kind) => {
              const rows = data.categories.filter((c) => c.kind === kind);
              if (rows.length === 0) return null;
              return (
                <Section key={kind} title={t(`finance.overview.byCategory.${kind}`)}>
                  <List>
                    {rows.map((c) => (
                      <ListRow
                        key={c.category.id}
                        href={`/finanzen/buchungen?kategorie=${c.category.id}&jahr=${data.year}`}
                        title={c.category.name}
                        description={previous(c.previous_cents)}
                        trailing={formatEuro(c.cents)}
                      />
                    ))}
                  </List>
                </Section>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}
